import React, { useState } from 'react';
import { useNavigate, Navigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import BaseLayout from './BaseLayout';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import { apiErrorMessage } from './prescriptionForm';
import { isEmailish } from './authForm';
import '../styles/Login.css';

/**
 * Map a failed sign-in to something the user can act on.
 *
 * Every failure used to become "Invalid credentials", so an outage or a CORS
 * misconfiguration told the user their password was wrong. A 401 still shows a
 * deliberately ambiguous message — the API returns the same body for a wrong
 * password and an unknown email (see api-contract.md AUTH-11), and the client
 * must not undo that by distinguishing them — but transport and server failures
 * now say what they are.
 */
export function signInErrorMessage(error) {
  if (error?.response?.status === 401) {
    // The second sentence is static, so it is true of every 401 and tells a
    // prober nothing. An unconfirmed account is refused with the same body as
    // a wrong password (AUTH-30); without this, somebody who signed up a
    // minute ago reads that as their new password not working.
    return 'Invalid email or password. Just signed up? Open the link we emailed you first.';
  }
  if (!error?.response) {
    return 'Could not reach the server. Check your connection and try again.';
  }
  // A 400 here is a validation failure — almost always a malformed email. The
  // server answers with a gin validator string ("Key: 'Email' Error:Field
  // validation for 'Email' failed on the 'email' tag"), which is not something
  // to put in front of a person. Turn the email case into plain words, and give
  // any other 400 a readable fallback rather than the raw passthrough.
  if (error.response.status === 400) {
    const raw = error.response.data?.error || '';
    if (/email/i.test(raw)) {
      return 'Enter a valid email address.';
    }
    return 'Some of those details were not accepted. Check them and try again.';
  }
  return apiErrorMessage(error, 'Something went wrong signing you in.');
}

const Login = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  // Register redirects here after creating an account and hands over a message
  // to show. Without this the sign-up flow ends on a bare login form with no
  // indication anything worked.
  const notice = useLocation().state?.notice;
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirects before rendering, rather than the useEffect this replaced, which
  // ran after the first paint and so flashed the login form at an already
  // signed-in user.
  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear the previous failure as soon as the user edits, so a stale "invalid
    // password" does not sit above a corrected one.
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // Catch a malformed email before a round trip, and say so in plain words.
    // The form is noValidate, so nothing else does — and the server's rejection
    // is an unreadable validator string. Mirrors the check Register runs.
    if (!isEmailish(formData.email.trim())) {
      setError('Enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    try {
      const response = await axios.post(buildApiUrl('/auth/login'), formData);
      const { token, user } = response.data ?? {};

      // A 200 without a token is a contract violation, not a success. Destructuring
      // straight into user.username threw a TypeError here instead.
      if (!token || !user?.username) {
        setError('The server did not return a valid session. Try again.');
        return;
      }

      // login() owns persistence. This used to write the token and username to
      // localStorage itself and *then* call login(), which writes them again —
      // the screen duplicating the context's job and knowing its storage keys.
      login(token, user.username);
      navigate('/home');
    } catch (err) {
      setError(signInErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BaseLayout>
      <div className="login">
        <div className="card login-card">
          <header className="login-header">
            <span className="pill">Sign in</span>
            <h1 className="page-title login-title">Welcome back</h1>
            <p className="meta-copy login-subtitle">
              Sign in to reach your programs and training log.
            </p>
          </header>

          {notice && !error && (
            <div className="status-success" role="status">
              {notice}
            </div>
          )}

          {error && (
            <div className="status-error" role="alert">
              {error}
            </div>
          )}

          <form className="login-form" onSubmit={handleSubmit} noValidate>
            <div className="login-field">
              <label htmlFor="login-email">Email</label>
              <input
                id="login-email"
                className="field"
                type="email"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="you@example.com"
              />
            </div>

            <div className="login-field">
              <label htmlFor="login-password">Password</label>
              <input
                id="login-password"
                className="field"
                type="password"
                name="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Your password"
              />
            </div>

            <div className="login-actions">
              <button type="submit" className="btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Signing in…' : 'Sign in'}
              </button>
              <Link to="/register" className="btn-ghost login-register">
                Create account
              </Link>
            </div>

            {/* The only route to this screen. A reset flow nothing links to is
                a reset flow nobody finds, and the person who needs it is by
                definition locked out and cannot be told where it lives. */}
            <Link to="/forgot-password" className="login-forgot">
              Forgotten your password?
            </Link>
          </form>
        </div>
      </div>
    </BaseLayout>
  );
};

export default Login;
